import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { PromotionCampaignListItem, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-campaigns',
  imports: [AinPageComponent, AinStateComponent, TableModule, ButtonModule, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.campaigns.title')" [description]="t('sales.campaigns.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="selectedRows.set($event ?? [])">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.campaigns.fields.code') }}</th>
                <th>{{ t('sales.campaigns.fields.name') }}</th>
                <th>{{ t('sales.campaigns.fields.currency') }}</th>
                <th>{{ t('sales.campaigns.fields.budget') }}</th>
                <th>{{ t('sales.campaigns.fields.validFrom') }}</th>
                <th>{{ t('sales.campaigns.fields.validTo') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.code }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
                <td>{{ row.currencyCode }}</td>
                <td>{{ budgetLabel(row) }}</td>
                <td>{{ row.validFromUtc | ainDate }}</td>
                <td>{{ row.validToUtc ? (row.validToUtc | ainDate) : '-' }}</td>
                <td>{{ row.isActive ? t('sales.campaigns.active') : t('sales.campaigns.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
  `]
})
export class SalesCampaignsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<PromotionCampaignListItem[]>([]);
  readonly selectedRows = signal<PromotionCampaignListItem[]>([]);

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.api.listPromotionCampaigns().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.campaigns.errorLoad')); this.loading.set(false); }
    });
  }

  budgetLabel(row: PromotionCampaignListItem): string {
    if (row.budgetAmount == null) return '-';
    return `${row.budgetConsumed} / ${row.budgetAmount}`;
  }

  openCreate(): void { void this.router.navigate(['/sales/campaigns/new']); }
  openSelected(): void { const row = this.selectedRows()[0]; if (row) this.openDetail(row.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/campaigns', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
